import { motion, useInView } from "framer-motion";
import { useRef } from "react";

const audiences = [
  {
    id: "teams",
    label: "For companies",
    headline: "Give your team back the hours lost to repeat work.",
    body: "Roll out twins for managers, sellers, and operators who already do great work but spend too much time on follow-ups and context switching.",
    stat: "Teams",
    dark: true,
    testId: "audience-teams",
  },
  {
    id: "individuals",
    label: "For you",
    headline: "Run work and life from one place that actually knows you.",
    body: "Connect the apps you already use. Keep client work moving without letting home admin, travel, and family logistics slide.",
    stat: "Personal",
    dark: false,
    testId: "audience-individuals",
  },
];

export default function Audiences() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section
      id="audiences"
      data-testid="audiences-section"
      ref={ref}
      className="relative py-24 sm:py-32 px-6 sm:px-8 bg-teamie-surface scroll-mt-24"
    >
      <div className="max-w-7xl mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="font-mono text-xs tracking-[0.2em] uppercase text-teamie-accent mb-6"
        >
          Work, home, or both
        </motion.p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {audiences.map((audience, index) => (
            <motion.div
              key={audience.id}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.15 + index * 0.12 }}
              data-testid={audience.testId}
              className={`relative overflow-hidden rounded-lg border p-10 sm:p-12 min-h-[280px] flex flex-col justify-between ${
                audience.dark
                  ? "bg-teamie-dark border-teamie-dark text-teamie-bg"
                  : "bg-teamie-bg border-teamie-border text-teamie-text"
              }`}
            >
              <div>
                <span
                  className={`inline-block font-mono text-[10px] tracking-[0.2em] uppercase mb-6 ${
                    audience.dark ? "text-teamie-bg/50" : "text-teamie-text-secondary"
                  }`}
                >
                  {audience.label}
                </span>
                <h3
                  className={`font-heading text-2xl sm:text-3xl tracking-tight leading-tight mb-5 ${
                    audience.dark ? "text-teamie-bg" : "text-teamie-text"
                  }`}
                >
                  {audience.headline}
                </h3>
                <p
                  className={`font-body text-sm sm:text-base leading-relaxed max-w-md ${
                    audience.dark ? "text-teamie-bg/70" : "text-teamie-text-secondary"
                  }`}
                >
                  {audience.body}
                </p>
              </div>
              <p
                className={`font-mono text-xs tracking-[0.15em] uppercase mt-8 ${
                  audience.dark ? "text-teamie-bg/40" : "text-teamie-accent"
                }`}
              >
                {audience.stat}
              </p>

              {audience.dark && (
                <div
                  className="absolute -bottom-12 -right-12 w-48 h-48 rounded-full border border-teamie-bg/10"
                  aria-hidden="true"
                />
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
